import { useCallback, useEffect, useMemo, useState } from "react";
import { IconAdjustmentsHorizontal, IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import { CategoryFieldConfigDialog } from "./category-field-config-dialog";
import { Button } from "../../../../components/common/button";
import { Dialog } from "../../../../components/common/dialog";
import { Input } from "../../../../components/common/input";
import { Select } from "../../../../components/forms/select";
import { SimpleTable } from "../../../../components/common/simple-table";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { Text } from "../../../../components/common/typography";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import { categoriesCrud, type CategoryRow } from "../../api/admin-catalog-api";

/**
 * Build a Set of IDs that are descendants of `rootId` (inclusive). Used to
 * forbid picking yourself or any of your children as a parent — that would
 * create a cycle in the Odoo product.category tree.
 */
function descendantIds(rows: CategoryRow[], rootId: number): Set<number> {
  const childrenByParent = new Map<number, number[]>();
  for (const r of rows) {
    const pid = r.parent?.id ?? null;
    if (pid !== null) {
      const list = childrenByParent.get(pid) ?? [];
      list.push(r.id);
      childrenByParent.set(pid, list);
    }
  }
  const out = new Set<number>([rootId]);
  const stack = [rootId];
  while (stack.length > 0) {
    const id = stack.pop()!;
    for (const child of childrenByParent.get(id) ?? []) {
      if (!out.has(child)) {
        out.add(child);
        stack.push(child);
      }
    }
  }
  return out;
}

export function CategoriesPanel() {
  const [rows, setRows] = useState<CategoryRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState<CategoryRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CategoryRow | null>(null);
  const [fieldConfigTarget, setFieldConfigTarget] = useState<CategoryRow | null>(null);
  // Stable identity so the dialog's load effect doesn't re-run on every render.
  const closeFieldConfig = useCallback(() => setFieldConfigTarget(null), []);

  const categoryNameById = (id: number) =>
    rows?.find((r) => r.id === id)?.name;

  const reload = async () => {
    setLoadError(null);
    try {
      setRows(await categoriesCrud.list());
    } catch (e) {
      setLoadError((e as Error)?.message ?? "Failed to load");
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const openCreate = () => {
    setCreating(true);
    setEditing(null);
    setName("");
    setParentId("");
  };

  const openEdit = (row: CategoryRow) => {
    setEditing(row);
    setCreating(false);
    setName(row.name);
    setParentId(row.parent?.id ? String(row.parent.id) : "");
  };

  const close = () => {
    setEditing(null);
    setCreating(false);
  };

  const isOpen = creating || editing !== null;

  const parentOptions = useMemo(() => {
    if (!rows) return [];
    if (!editing) return rows;
    const forbidden = descendantIds(rows, editing.id);
    return rows.filter((r) => !forbidden.has(r.id));
  }, [rows, editing]);

  const submit = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        parentId: parentId ? Number(parentId) : null,
      };
      if (editing) {
        await categoriesCrud.update(editing.id, payload);
        showSuccessToast("Category updated");
      } else {
        await categoriesCrud.create(payload);
        showSuccessToast("Category created");
      }
      close();
      await reload();
    } catch (e) {
      showErrorToast((e as Error)?.message ?? "Save failed", e);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await categoriesCrud.remove(deleteTarget.id);
      showSuccessToast("Category archived");
      setDeleteTarget(null);
      await reload();
    } catch (e) {
      showErrorToast((e as Error)?.message ?? "Delete failed", e);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: "id",
        label: "ID",
        className: "w-20",
        render: (r: CategoryRow) => <Text variant="body">{r.id}</Text>,
      },
      {
        key: "name",
        label: "Name",
        className: "flex-1",
        render: (r: CategoryRow) => <Text variant="body">{r.name}</Text>,
      },
      {
        key: "parent",
        label: "Parent",
        className: "flex-1",
        render: (r: CategoryRow) => (
          <Text variant="body" tone={r.parent ? "dark" : "muted"}>
            {r.parent?.name ?? "— root —"}
          </Text>
        ),
      },
      {
        key: "path",
        label: "Path",
        className: "flex-1",
        render: (r: CategoryRow) => (
          <Text variant="body" tone="muted">
            {r.completeName ?? r.displayName ?? r.name}
          </Text>
        ),
      },
      {
        key: "actions",
        label: "",
        className: "w-48 flex justify-end gap-2",
        render: (r: CategoryRow) => (
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="xs"
              leftIcon={<IconAdjustmentsHorizontal size={16} />}
              onClick={() => setFieldConfigTarget(r)}
            >
              Fields
            </Button>
            <Button
              variant="ghost"
              size="xs"
              leftIcon={<IconEdit size={16} />}
              onClick={() => openEdit(r)}
            >
              Edit
            </Button>
            <Button
              variant="ghost"
              size="xs"
              leftIcon={<IconTrash size={16} />}
              onClick={() => setDeleteTarget(r)}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <Text variant="bodyBold" tone="dark">
          Categories{rows ? ` (${rows.length})` : ""}
        </Text>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<IconPlus size={16} />}
          onClick={openCreate}
        >
          Add new
        </Button>
      </div>

      {!rows && !loadError && <ShimmerLoader message="Loading categories…" lines={4} />}
      {loadError && (
        <Text variant="body" tone="body" className="text-accentError">
          {loadError}
        </Text>
      )}
      {rows && rows.length > 0 && (
        <SimpleTable
          columns={columns}
          data={rows}
          keyExtractor={(r) => String(r.id)}
        />
      )}
      {rows && rows.length === 0 && (
        <Text variant="body" tone="muted">
          No categories yet — click "Add new" to create one.
        </Text>
      )}

      <Dialog
        open={isOpen}
        setOpen={(o) => {
          if (!o) close();
        }}
        title={editing ? "Edit category" : "New category"}
        showButtons
        buttonText={submitting ? "Saving…" : "Save"}
        onConfirm={submit}
        cancelText="Cancel"
        showCloseButton
        content={
          <div className="flex flex-col gap-3 py-2">
            <Input
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
            <Select
              name="parentId"
              label="Parent category"
              placeholder="— root —"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
              options={parentOptions.map((p) => ({
                value: String(p.id),
                label: p.completeName ?? p.displayName ?? p.name,
              }))}
              density="compact"
              helperText={editing ? "Self and descendants are hidden to prevent cycles." : undefined}
            />
          </div>
        }
      />

      <Dialog
        open={deleteTarget !== null}
        setOpen={(o) => {
          if (!o) setDeleteTarget(null);
        }}
        title={`Delete ${deleteTarget?.name ?? ""}?`}
        text="The category will be removed from Odoo. Existing products that already use it are unaffected."
        showButtons
        buttonText="Delete"
        onConfirm={confirmDelete}
        cancelText="Cancel"
      />

      <CategoryFieldConfigDialog
        open={fieldConfigTarget !== null}
        categoryId={fieldConfigTarget?.id ?? null}
        categoryName={fieldConfigTarget?.name ?? ""}
        onClose={closeFieldConfig}
        categoryNameById={categoryNameById}
      />
    </div>
  );
}
